/**
 * Slide-in cart drawer.
 *
 * Opens on add-to-cart, on the header cart icon, and renders from /cart.js.
 * Quantity changes and removals go through /cart/change.js and repaint in place.
 */
(function () {
  'use strict';

  var root, panel, itemsEl, countEl, totalEl, footEl, emptyEl;
  var lastFocused = null;

  document.addEventListener('DOMContentLoaded', function () {
    root = document.querySelector('[data-nova-drawer]');
    if (!root) return;

    panel = root.querySelector('[data-nova-drawer-panel]');
    itemsEl = root.querySelector('[data-nova-drawer-items]');
    countEl = root.querySelector('[data-nova-drawer-count]');
    totalEl = root.querySelector('[data-nova-drawer-total]');
    footEl = root.querySelector('[data-nova-drawer-foot]');
    emptyEl = root.querySelector('[data-nova-drawer-empty]');

    root.querySelectorAll('[data-nova-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) close();
      if (e.key === 'Tab' && !root.hidden) trapFocus(e);
    });

    // Header cart icon opens the drawer rather than navigating.
    document.querySelectorAll('.nova-header__cart').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openAndRefresh();
      });
    });

    // The buy button fires this once the items are in.
    document.addEventListener('nova:cart-updated', openAndRefresh);

    itemsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-nova-qty], [data-nova-remove]');
      if (!btn) return;

      var line = Number(btn.dataset.line);
      if (btn.hasAttribute('data-nova-remove')) {
        change(line, 0);
      } else {
        change(line, Number(btn.dataset.qty));
      }
    });
  });

  function openAndRefresh() {
    open();
    refresh();
  }

  function open() {
    lastFocused = document.activeElement;
    root.hidden = false;
    // Next frame so the transition actually runs.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      document.documentElement.classList.add('nova-drawer-open');
      if (panel) panel.focus();
    });
  }

  function close() {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('nova-drawer-open');
    window.setTimeout(function () {
      root.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }, 260);
  }

  function refresh() {
    fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () { /* leave the last painted state */ });
  }

  function change(line, quantity) {
    fetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        // Distinct from nova:cart-updated, which reopens the drawer.
        document.dispatchEvent(new CustomEvent('nova:cart-changed'));
      })
      .catch(function () { /* no-op */ });
  }

  function render(cart) {
    if (!cart || !itemsEl) return;

    countEl.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';
    totalEl.textContent = money(cart.total_price, cart.currency);

    var empty = cart.item_count === 0;
    emptyEl.hidden = !empty;
    footEl.hidden = empty;

    itemsEl.innerHTML = cart.items.map(function (item, i) {
      var line = i + 1;
      var img = item.image
        ? '<img src="' + resize(item.image, 160) + '" alt="" width="64" height="64" loading="lazy">'
        : '<span class="nova-drawer__noimg" aria-hidden="true"></span>';

      return '' +
        '<li class="nova-drawer__item">' +
          '<span class="nova-drawer__thumb">' + img + '</span>' +
          '<span class="nova-drawer__meta">' +
            '<strong>' + escapeHtml(item.product_title) + '</strong>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<small>' + escapeHtml(item.variant_title) + '</small>' : '') +
            '<span class="nova-drawer__qty">' +
              '<button type="button" data-nova-qty data-line="' + line + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">&minus;</button>' +
              '<b>' + item.quantity + '</b>' +
              '<button type="button" data-nova-qty data-line="' + line + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
            '</span>' +
          '</span>' +
          '<span class="nova-drawer__line">' +
            '<strong>' + money(item.final_line_price, cart.currency) + '</strong>' +
            '<button type="button" data-nova-remove data-line="' + line + '">Remove</button>' +
          '</span>' +
        '</li>';
    }).join('');

    updateShipping(cart);
  }

  function updateShipping(cart) {
    var wrap = root.querySelector('[data-nova-drawer-ship]');
    if (!wrap) return;

    var threshold = Number(wrap.dataset.threshold || 0) * 100;
    if (!threshold) { wrap.hidden = true; return; }

    var text = root.querySelector('[data-nova-drawer-ship-text]');
    var bar = root.querySelector('[data-nova-drawer-ship-bar]');
    var left = threshold - cart.total_price;

    wrap.hidden = false;
    if (left > 0) {
      text.textContent = money(left, cart.currency) + ' away from free shipping';
      bar.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
    } else {
      text.textContent = 'Free shipping unlocked';
      bar.style.width = '100%';
    }
  }

  function route(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function resize(url, width) {
    return url.replace(/(\.[a-z]{3,4})(\?.*)?$/i, '_' + width + 'x$1$2');
  }

  function money(cents, currency) {
    var value = (cents / 100).toFixed(2);
    return (currency === 'USD' || !currency ? '$' : '$') + value;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function trapFocus(e) {
    var focusables = panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
})();
